/*
 * Navigation layer for the product media gallery.
 *
 * Sits inside <media-gallery> and drives it through whichever interaction the
 * merchant enabled: custom arrows placed over the image or beside the
 * thumbnails, pointer dragging, or swipe only. The gallery renders in two very
 * different modes depending on the layout setting, so every action is resolved
 * against the mode detected at runtime:
 *
 *   swap mode   - only the active <li> is displayed, the rest are display:none
 *                 (desktop "thumbnail" / "thumbnail slider" layouts)
 *   scroll mode - the list is a horizontal scroll-snap strip (mobile slider)
 */
if (!customElements.get('ss-gallery-nav')) {
  customElements.define(
    'ss-gallery-nav',
    class SSGalleryNav extends HTMLElement {
      connectedCallback() {
        if (this.ssInitialised) return;
        this.ssInitialised = true;

        this.gallery = this.closest('media-gallery');
        if (!this.gallery) return;

        this.list = this.gallery.querySelector('.product__media-list');
        if (!this.list) return;

        this.viewer = this.gallery.querySelector('[id^="GalleryViewer"]');
        this.thumbs = this.gallery.querySelector('[id^="GalleryThumbnails"]');
        this.loop = this.dataset.loop === 'true';
        this.dragThreshold = 45;

        this.prevButtons = Array.from(this.gallery.querySelectorAll('[data-ss-gallery-prev]'));
        this.nextButtons = Array.from(this.gallery.querySelectorAll('[data-ss-gallery-next]'));

        this.bindButtons();
        this.bindKeyboard();
        if (this.dataset.drag === 'true') this.bindDrag();

        this.syncHandler = this.throttle(() => this.sync(), 100);
        this.list.addEventListener('scroll', this.syncHandler, { passive: true });
        this.viewer?.addEventListener('slideChanged', this.syncHandler);
        this.thumbs?.addEventListener('click', this.syncHandler);

        // Variant switches re-order and replace media items.
        this.observer = new MutationObserver(this.syncHandler);
        this.observer.observe(this.list, { childList: true });

        this.sync();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      /* ----------------------------------------------------------- helpers */

      items() {
        return Array.from(this.list.querySelectorAll('.product__media-item'));
      }

      isRtl() {
        return getComputedStyle(this.list).direction === 'rtl';
      }

      // Non-active slides are display:none in the thumbnail layouts, which is
      // the only reliable signal that navigation must swap rather than scroll.
      isSwapMode() {
        const items = this.items();
        if (items.length < 2) return false;
        const inactive = items.find((el) => !el.classList.contains('is-active'));
        return inactive ? inactive.offsetParent === null : false;
      }

      throttle(fn, wait) {
        let queued = false;
        return () => {
          if (queued) return;
          queued = true;
          setTimeout(() => {
            queued = false;
            fn();
          }, wait);
        };
      }

      /* -------------------------------------------------------- navigation */

      currentIndex() {
        const items = this.items();
        if (!items.length) return 0;

        if (this.isSwapMode()) {
          const active = items.findIndex((el) => el.classList.contains('is-active'));
          return active < 0 ? 0 : active;
        }

        const listLeft = this.list.getBoundingClientRect().left;
        let best = 0;
        let bestDistance = Infinity;
        items.forEach((el, index) => {
          const distance = Math.abs(el.getBoundingClientRect().left - listLeft);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = index;
          }
        });
        return best;
      }

      step(direction) {
        this.go(this.currentIndex() + direction);
      }

      go(index) {
        const items = this.items();
        if (!items.length) return;

        let target = index;
        if (this.loop) {
          target = ((target % items.length) + items.length) % items.length;
        } else {
          target = Math.max(0, Math.min(items.length - 1, target));
        }

        const item = items[target];
        if (!item) return;

        if (this.isSwapMode() && typeof this.gallery.setActiveMedia === 'function' && item.dataset.mediaId) {
          this.gallery.setActiveMedia(item.dataset.mediaId, false);
        } else {
          this.scrollToItem(item);
          this.markActive(item);
        }
        this.sync();
      }

      // scrollBy with a physical delta behaves consistently across browsers in
      // both directions, unlike assigning scrollLeft in an RTL container.
      scrollToItem(item) {
        const delta = item.getBoundingClientRect().left - this.list.getBoundingClientRect().left;
        if (Math.abs(delta) < 1) return;
        this.list.scrollBy({ left: delta, behavior: 'smooth' });
      }

      markActive(item) {
        this.items().forEach((el) => el.classList.toggle('is-active', el === item));
        if (!this.thumbs || !item.dataset.mediaId) return;
        const thumbnail = this.thumbs.querySelector(`[data-target="${item.dataset.mediaId}"]`);
        if (thumbnail && typeof this.gallery.setActiveThumbnail === 'function') {
          this.gallery.setActiveThumbnail(thumbnail);
        }
      }

      sync() {
        const total = this.items().length;
        const index = this.currentIndex();
        const atStart = !this.loop && index <= 0;
        const atEnd = !this.loop && index >= total - 1;
        this.prevButtons.forEach((button) => {
          button.disabled = atStart;
        });
        this.nextButtons.forEach((button) => {
          button.disabled = atEnd;
        });
        this.dataset.index = index;
      }

      /* ---------------------------------------------------------- bindings */

      bindButtons() {
        this.prevButtons.forEach((button) =>
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.step(-1);
          })
        );
        this.nextButtons.forEach((button) =>
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.step(1);
          })
        );
      }

      bindKeyboard() {
        const host = this.viewer || this.list;
        host.setAttribute('tabindex', host.getAttribute('tabindex') || '0');
        host.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          if (event.target.closest('input, textarea, select')) return;
          const forward = this.isRtl() ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
          event.preventDefault();
          this.step(forward ? 1 : -1);
        });
      }

      bindDrag() {
        const host = this.viewer || this.list;
        let active = false;
        let axisLocked = false;
        let swap = false;
        let startX = 0;
        let startY = 0;
        let moved = 0;
        let startScroll = 0;

        const reset = () => {
          active = false;
          axisLocked = false;
          this.gallery.classList.remove('ss-gallery-dragging');
          this.items().forEach((item) => {
            item.style.transform = '';
          });
        };

        host.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          if (event.target.closest('button, a, input, [data-ss-gallery-prev], [data-ss-gallery-next]')) return;
          swap = this.isSwapMode();
          // Touch already scrolls the strip natively; only take over when the
          // layout swaps slides instead of scrolling them.
          if (event.pointerType === 'touch' && !swap) return;
          if (this.items().length < 2) return;

          active = true;
          axisLocked = false;
          moved = 0;
          startX = event.clientX;
          startY = event.clientY;
          startScroll = this.list.scrollLeft;
        });

        host.addEventListener(
          'pointermove',
          (event) => {
            if (!active) return;
            const dx = event.clientX - startX;
            const dy = event.clientY - startY;

            if (!axisLocked) {
              if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
              if (Math.abs(dy) > Math.abs(dx)) {
                reset();
                return;
              }
              axisLocked = true;
              this.gallery.classList.add('ss-gallery-dragging');
              host.setPointerCapture?.(event.pointerId);
            }

            moved = dx;
            if (swap) {
              const current = this.items()[this.currentIndex()];
              if (current) current.style.transform = `translateX(${dx * 0.35}px)`;
            } else {
              this.list.scrollLeft = startScroll - dx;
            }
            event.preventDefault();
          },
          { passive: false }
        );

        const finish = () => {
          if (!active) return;
          const distance = moved;
          const wasLocked = axisLocked;
          const wasSwap = swap;
          reset();
          if (!wasLocked) return;

          if (wasSwap) {
            if (Math.abs(distance) > this.dragThreshold) {
              const forward = this.isRtl() ? distance > 0 : distance < 0;
              this.step(forward ? 1 : -1);
            }
          } else {
            this.go(this.currentIndex());
          }

          if (Math.abs(distance) > 6) {
            const swallow = (event) => {
              event.preventDefault();
              event.stopPropagation();
            };
            host.addEventListener('click', swallow, { capture: true, once: true });
            setTimeout(() => host.removeEventListener('click', swallow, true), 400);
          }
        };

        host.addEventListener('pointerup', finish);
        host.addEventListener('pointercancel', reset);
        host.addEventListener('lostpointercapture', reset);
      }
    }
  );
}
